/**
 * WeekModuleView
 *
 * Displays all 7 modules for a specific week in preview mode.
 * Teachers can click on any module to view its content and quiz.
 */

import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Breadcrumbs,
  Link,
  Chip,
  Skeleton,
  Alert,
  Paper,
  Avatar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";

// Icons
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import QuizIcon from "@mui/icons-material/Quiz";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Module-specific icons
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CreateIcon from "@mui/icons-material/Create";
import HeadsetIcon from "@mui/icons-material/Headset";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import GavelIcon from "@mui/icons-material/Gavel";
import TranslateIcon from "@mui/icons-material/Translate";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";

// Services
import {
  previewService,
  PreviewWeekDetail,
  PreviewModuleSummary,
  getProgramDisplayName,
  getModuleColor,
} from "../../../services/previewService";

// Context
import { usePreview } from "../../../context/PreviewContext";

// ============================================================================
// Styled Components
// ============================================================================

const WeekInfoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  background: `linear-gradient(135deg, ${theme.palette.info.light}15, ${theme.palette.info.main}05)`,
  borderLeft: `4px solid ${theme.palette.info.main}`,
}));

const ModuleCard = styled(Card)<{ modulecolor: string }>(
  ({ theme, modulecolor }) => ({
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
    borderTop: `3px solid ${modulecolor}`,
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[6],
    },
  })
);

const ModuleIcon = styled(Avatar)<{ modulecolor: string }>(
  ({ modulecolor }) => ({
    width: 56,
    height: 56,
    backgroundColor: `${modulecolor}20`,
    color: modulecolor,
    marginBottom: 12,
  })
);

const StatBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  fontSize: "0.875rem",
}));

// ============================================================================
// Constants
// ============================================================================

const MODULE_ICONS: Record<string, React.ReactNode> = {
  reading: <MenuBookIcon />,
  writing: <CreateIcon />,
  listening: <HeadsetIcon />,
  speaking: <RecordVoiceOverIcon />,
  grammar: <GavelIcon />,
  vocabulary: <TranslateIcon />,
  review: <AssignmentTurnedInIcon />,
};

// Standard module order for consistent display
const MODULE_ORDER = [
  "reading",
  "writing",
  "listening",
  "speaking",
  "grammar",
  "vocabulary",
  "review",
];

// ============================================================================
// Component
// ============================================================================

const WeekModuleView: React.FC = () => {
  const navigate = useNavigate();
  const { program, weekNumber } = useParams<{
    program: string;
    weekNumber: string;
  }>();

  // Get cache functions from context
  const { getCachedWeek, setCachedWeek } = usePreview();

  // State
  const [weekData, setWeekData] = useState<PreviewWeekDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const weekNum = parseInt(weekNumber || "1", 10);

  // Fetch week modules on mount - check cache first
  useEffect(() => {
    const fetchWeekModules = async () => {
      if (!program || !weekNumber) return;

      // Check cache first
      const cached = getCachedWeek(program, weekNum);
      if (cached) {
        console.log("[Preview] Using cached week data:", program, weekNum);
        setWeekData(cached);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await previewService.getWeekModules(program, weekNum);
        setWeekData(data);

        // Cache the response
        setCachedWeek(program, weekNum, data);
        console.log("[Preview] Cached week data:", program, weekNum);
      } catch (err: any) {
        console.error("Error fetching week modules:", err);
        setError(
          err.response?.status === 400
            ? "Invalid program or week number."
            : "Failed to load week content. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchWeekModules();
  }, [program, weekNumber, weekNum, getCachedWeek, setCachedWeek]);

  // Navigate to module detail
  const handleModuleClick = (moduleType: string) => {
    navigate(`/teacher/preview/${program}/week/${weekNumber}/${moduleType}`);
  };

  // Sort modules in standard order
  const sortedModules = React.useMemo(() => {
    if (!weekData?.modules) return [];

    return [...weekData.modules].sort((a, b) => {
      const indexA = MODULE_ORDER.indexOf(a.moduleType.toLowerCase());
      const indexB = MODULE_ORDER.indexOf(b.moduleType.toLowerCase());
      return (indexA === -1 ? 99 : indexA) - (indexB === -1 ? 99 : indexB);
    });
  }, [weekData?.modules]);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <Container maxWidth="lg">
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 2 }}
      >
        <Link
          component={RouterLink}
          to="/teacher/preview"
          underline="hover"
          color="inherit"
        >
          Content Preview
        </Link>
        <Link
          component={RouterLink}
          to={`/teacher/preview/${program}`}
          underline="hover"
          color="inherit"
          sx={{ display: "flex", alignItems: "center" }}
        >
          <ArrowBackIcon sx={{ mr: 0.5, fontSize: 18 }} />
          {weekData?.programName || getProgramDisplayName(program || "")}
        </Link>
        <Typography color="text.primary" fontWeight="medium">
          Week {weekNumber}
        </Typography>
      </Breadcrumbs>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Week Info Header */}
      {loading ? (
        <Skeleton
          variant="rectangular"
          height={100}
          sx={{ mb: 3, borderRadius: 1 }}
        />
      ) : weekData ? (
        <WeekInfoCard elevation={0}>
          <Box display="flex" alignItems="center" gap={2} mb={1}>
            <VisibilityIcon color="info" />
            <Typography variant="h5" fontWeight="bold">
              {weekData.weekName}
            </Typography>
            <Chip label="Preview Mode" size="small" color="info" />
          </Box>
          <Typography variant="body1" color="text.secondary">
            {weekData.programName} • {weekData.modules.length} modules available
          </Typography>
        </WeekInfoCard>
      ) : null}

      {/* Instruction */}
      <Box mb={3}>
        <Typography variant="body1" color="text.secondary">
          Select a module to preview its learning content and quiz questions.
        </Typography>
      </Box>

      {/* Module Cards Grid */}
      <Grid container spacing={3}>
        {loading ? (
          // Loading skeletons
          <>
            {MODULE_ORDER.map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton
                  variant="rectangular"
                  height={220}
                  sx={{ borderRadius: 1 }}
                />
              </Grid>
            ))}
          </>
        ) : (
          // Module cards
          sortedModules.map((module) => {
            const moduleType = module.moduleType.toLowerCase();
            const color = getModuleColor(moduleType);

            return (
              <Grid item xs={12} sm={6} md={4} key={module.moduleId}>
                <ModuleCard modulecolor={color}>
                  <CardActionArea
                    onClick={() => handleModuleClick(moduleType)}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <CardContent sx={{ width: "100%", flexGrow: 1 }}>
                      {/* Module Icon */}
                      <ModuleIcon modulecolor={color}>
                        {MODULE_ICONS[moduleType] || <MenuBookIcon />}
                      </ModuleIcon>

                      {/* Module Name */}
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {module.moduleName}
                      </Typography>

                      {/* Description */}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          minHeight: 40,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {module.description ||
                          `${module.moduleName} module content`}
                      </Typography>

                      {/* Stats Row */}
                      <Box display="flex" gap={2} flexWrap="wrap">
                        <StatBox>
                          <AccessTimeIcon sx={{ fontSize: 18 }} />
                          {module.estimatedTime || 30} min
                        </StatBox>
                        <StatBox>
                          <QuizIcon sx={{ fontSize: 18 }} />
                          {module.questionCount || 0} questions
                        </StatBox>
                      </Box>

                      {/* Content Availability Chips */}
                      <Box mt={2} display="flex" gap={1}>
                        {module.hasLearningContent !== false && (
                          <Chip
                            icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                            label="Learning Content"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                        {module.hasQuiz !== false &&
                          module.questionCount > 0 && (
                            <Chip
                              icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                              label="Quiz"
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </ModuleCard>
              </Grid>
            );
          })
        )}
      </Grid>

      {/* Empty State */}
      {!loading && !error && sortedModules.length === 0 && (
        <Box textAlign="center" py={8}>
          <MenuBookIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No modules available for this week
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default WeekModuleView;
